/**
 * Google Cloud Monitoring Integration
 * Monitor infrastructure health, performance, and uptime
 * 
 * Now uses monitoringService to fetch real data from Firestore via Cloud Functions
 */

import { monitoringService } from './monitoringService';

export interface MetricData {
  timestamp: Date;
  value: number;
  labels?: Record<string, string>;
}

export interface Alert {
  id: string;
  name: string;
  condition: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'active' | 'resolved';
  triggeredAt: Date;
  resolvedAt?: Date;
  message: string;
  source?: string;
}

export interface UpptimeCheck {
  id?: string;
  name: string;
  url?: string;
  status: 'up' | 'down' | 'degraded';
  responseTime: number;
  lastChecked: Date;
  uptime: number; // percentage
}

export interface SystemMetrics {
  cpu: {
    usage: number;
    trend: 'up' | 'down' | 'stable';
  };
  memory: {
    usage: number;
    total: number;
    trend: 'up' | 'down' | 'stable';
  };
  network: {
    requestsPerSecond: number;
    errorRate: number;
    avgLatency: number;
  };
  database: {
    connections: number;
    queryTime: number;
    writes: number;
    reads: number;
  };
}

class CloudMonitoringService {
  private projectId: string = 'un-dollar-rentflow-dev';
  
  /**
   * Get current system metrics from Firestore
   */
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      const { metrics } = await monitoringService.getSystemMetrics();
      return metrics;
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      // Return default values on error
      return {
        cpu: { usage: 0, trend: 'stable' },
        memory: { usage: 0, total: 4.0, trend: 'stable' },
        network: { requestsPerSecond: 0, errorRate: 0, avgLatency: 0 },
        database: { connections: 0, queryTime: 0, writes: 0, reads: 0 },
      };
    }
  }

  /**
   * Get uptime checks status from Firestore
   */
  async getUptimeChecks(): Promise<UpptimeCheck[]> {
    try {
      const checks = await monitoringService.getUptimeChecks();
      return checks.map(check => ({
        id: check.id,
        name: check.name,
        url: check.url,
        status: check.status,
        responseTime: check.responseTime,
        lastChecked: new Date(check.lastChecked),
        uptime: check.uptime,
      }));
    } catch (error) {
      console.error('Error fetching uptime checks:', error);
      return [];
    }
  }

  /**
   * Get active alerts from Firestore
   */
  async getActiveAlerts(): Promise<Alert[]> {
    try {
      return await monitoringService.getActiveAlerts();
    } catch (error) {
      console.error('Error fetching active alerts:', error);
      return [];
    }
  }

  /**
   * Get recent alerts (last 7 days) from Firestore
   */
  async getRecentAlerts(): Promise<Alert[]> {
    try {
      return await monitoringService.getRecentAlerts(50);
    } catch (error) {
      console.error('Error fetching recent alerts:', error);
      return [];
    }
  }

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId: string, resolution?: string): Promise<boolean> {
    try {
      const result = await monitoringService.resolveAlert(alertId, resolution);
      return result.success;
    } catch (error) {
      console.error('Error resolving alert:', error);
      return false;
    }
  }

  /**
   * Get metric time series data from Firestore history
   */
  async getMetricTimeSeries(
    metricType: 'cpu' | 'memory' | 'requests' | 'errors' | 'latency',
    duration: '1h' | '24h' | '7d' | '30d' = '24h'
  ): Promise<MetricData[]> {
    try {
      return await monitoringService.getMetricTimeSeries(metricType, duration);
    } catch (error) {
      console.error('Error fetching metric time series:', error);
      return [];
    }
  }

  /**
   * Get logs from Firestore
   */
  async getLogs(
    severity?: 'ERROR' | 'WARNING' | 'INFO',
    limit: number = 100
  ): Promise<Array<{
    timestamp: Date;
    severity: string;
    message: string;
    resource: string;
  }>> {
    try {
      const logs = await monitoringService.getLogs({ 
        severity: severity || 'all', 
        limit 
      });
      return logs.map(log => ({
        timestamp: new Date(log.timestamp),
        severity: log.severity,
        message: log.message,
        resource: log.resource || 'system',
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      return [];
    }
  }

  /**
   * Get service health dashboard URL
   */
  getMonitoringDashboardUrl(): string {
    return monitoringService.getMonitoringDashboardUrl();
  }

  /**
   * Get logs explorer URL
   */
  getLogsExplorerUrl(): string {
    return monitoringService.getLogsExplorerUrl();
  }

  /**
   * Get trace explorer URL
   */
  getTraceExplorerUrl(): string {
    return monitoringService.getTraceExplorerUrl();
  }
}

export const cloudMonitoring = new CloudMonitoringService();
export default cloudMonitoring;
